import { FC, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FormattedMessage } from 'react-intl';
import { Navigate, useLocation, useNavigate } from 'react-router';

import { PATHS } from '@/shared/config';
import { useAppSelector, useMedia } from '@/shared/hooks';
import { Layout, MobileLayout, Spinner } from '@/shared/ui';

import { composedModel } from '@/entities/composed';
import { organizationModel } from '@/entities/organization';
import {
  hasTemplatesNavigationState,
  resolveTemplatesBackPath,
} from '@/entities/templates';

import { Header, MobileHeader } from '@/widgets/Header';
import {
  TemplateLibraryView,
  useCanEditTemplates,
} from '@/widgets/TranscriptorWorkspace';

import styles from './TranscriptorTemplates.module.scss';

// Where the layout in place shows a node that outlives it.
const NodeSlot: FC<{ node: HTMLElement }> = ({ node }) => {
  const slot = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    slot.current?.appendChild(node);
  }, [node]);

  return <div ref={slot} className={styles.slot} />;
};

export const TranscriptorTemplates: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useMedia();

  // The layout changes at the mobile breakpoint, which a window resized
  // across 1024px in landscape crosses too. The library is drawn once into
  // this node, which each layout shows, so it keeps its state, unsaved
  // template edits among it.
  const [libraryNode] = useState(() => {
    const node = document.createElement('div');
    node.className = styles.slot;
    return node;
  });

  const { isReady: isTranscriptorReady, isAvailable } = useAppSelector(
    composedModel.selectors.selectTranscriptorAvailability,
  );
  const v2Enabled = useAppSelector(
    organizationModel.selectors.selectCurrentTranscriptorV2Enabled,
  );
  const organizationLoading = useAppSelector(
    organizationModel.selectors.selectLoading,
  );
  const isOrganizationSettingsLoaded = useAppSelector(
    organizationModel.selectors.selectCurrentOrganizationSettingsLoaded,
  );

  // Scribe's availability is decided by the organization's flags, which may
  // still be the previous clinic's right after a clinic switch.
  const isReady =
    organizationLoading === 'succeeded' &&
    isOrganizationSettingsLoaded &&
    isTranscriptorReady;
  // The Recordings page's rule reads a patient, which this page does not
  // have.
  const canEdit = useCanEditTemplates();

  const handleBack = () => {
    const { state } = location;
    if (hasTemplatesNavigationState(state) && state.pushedFromBackPath) {
      navigate(-1);
      return;
    }
    navigate(resolveTemplatesBackPath(state, PATHS.patients));
  };

  if (!isReady) {
    return (
      <div className={styles.spinnerWrapper}>
        <Spinner className={styles.spinner} size={96} />
      </div>
    );
  }

  if (!isAvailable || !v2Enabled) {
    return <Navigate replace to={PATHS.main} />;
  }

  const title = (
    <h3 className="h3">
      <FormattedMessage
        id="workspace.templates.open"
        defaultMessage="Templates"
      />
    </h3>
  );
  const library = createPortal(
    <TemplateLibraryView canEdit={canEdit} />,
    libraryNode,
  );

  if (isMobile) {
    return (
      <>
        <MobileLayout>
          <MobileHeader showBackButton onBackButtonClick={handleBack} />

          <MobileLayout.Main>
            <div
              className={styles.mobileContainer}
              data-section="transcriptor-templates-page"
            >
              {title}
              <NodeSlot node={libraryNode} />
            </div>
          </MobileLayout.Main>
        </MobileLayout>
        {library}
      </>
    );
  }

  return (
    <>
      <Layout>
        <Layout.Header>
          <Header back onBackButtonClick={handleBack} />
        </Layout.Header>

        <Layout.Content>
          <Layout.Main>
            <div
              className={styles.container}
              data-section="transcriptor-templates-page"
            >
              {title}
              <NodeSlot node={libraryNode} />
            </div>
          </Layout.Main>
        </Layout.Content>
      </Layout>
      {library}
    </>
  );
};
